import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { AlertCircle, Link2, Plus, Loader2 } from "lucide-react";

export interface QBProductMatch {
  qbId: string;
  qbName: string;
  qbPrice?: number;
  matchConfidence: 'exact' | 'high' | 'partial';
}

export interface UnlinkedProductWithMatches {
  productId: number;
  productName: string;
  candidates: QBProductMatch[];
  willCreate?: boolean;
}

export interface ProductLinkDecision {
  productId: number;
  qbItemId: string;
}

interface QBProductLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unlinkedProducts: UnlinkedProductWithMatches[];
  onConfirm: (decisions: ProductLinkDecision[]) => void;
  isProcessing?: boolean;
}

export function QBProductLinkDialog({
  open,
  onOpenChange,
  unlinkedProducts,
  onConfirm,
  isProcessing = false,
}: QBProductLinkDialogProps) {
  const [decisions, setDecisions] = useState<Record<number, { qbItemId: string } | { createNew: true }>>({});

  // Auto-set "create new" for zero-match items — they have no candidates to choose from.
  useEffect(() => {
    const autoDecisions: Record<number, { createNew: true }> = {};
    for (const p of unlinkedProducts) {
      if (p.willCreate && !decisions[p.productId]) {
        autoDecisions[p.productId] = { createNew: true };
      }
    }
    if (Object.keys(autoDecisions).length > 0) {
      setDecisions(prev => ({ ...prev, ...autoDecisions }));
    }
  }, [unlinkedProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDecisionChange = (productId: number, value: string) => {
    if (value === 'create_new') {
      setDecisions(prev => ({ ...prev, [productId]: { createNew: true } }));
    } else {
      setDecisions(prev => ({ ...prev, [productId]: { qbItemId: value } }));
    }
  };

  const handleConfirm = () => {
    const finalDecisions: ProductLinkDecision[] = [];
    for (const product of unlinkedProducts) {
      const decision = decisions[product.productId];
      if (decision && 'qbItemId' in decision) {
        finalDecisions.push({
          productId: product.productId,
          qbItemId: decision.qbItemId,
        });
      }
    }
    onConfirm(finalDecisions);
  };

  const allDecisionsMade = unlinkedProducts.every(p => decisions[p.productId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            QuickBooks Product Matching
          </DialogTitle>
          <DialogDescription>
            Some products need your attention before invoices can be sent. Review each item below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {unlinkedProducts.map((product) => (
            <div key={product.productId} className="border rounded-lg p-4 space-y-3">
              <div className="font-medium text-lg">{product.productName}</div>

              {product.willCreate ? (
                // Zero-match: no QB item found at all — show confirmation prompt
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">No match found in QuickBooks</p>
                    <p className="text-sm text-amber-700 mt-1">
                      "{product.productName}" will be created as a new item in QuickBooks. If this product already exists there under a different name, cancel and correct the spelling or link it manually first.
                    </p>
                  </div>
                </div>
              ) : (
                // Multi-match: admin must pick which QB item to use
                <RadioGroup
                  value={
                    decisions[product.productId] && 'qbItemId' in decisions[product.productId]
                      ? (decisions[product.productId] as { qbItemId: string }).qbItemId
                      : decisions[product.productId] && 'createNew' in decisions[product.productId]
                        ? 'create_new'
                        : undefined
                  }
                  onValueChange={(value) => handleDecisionChange(product.productId, value)}
                >
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Link to existing QuickBooks item</p>
                    {product.candidates.map((candidate) => (
                      <div
                        key={candidate.qbId}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <RadioGroupItem value={candidate.qbId} id={`match-${product.productId}-${candidate.qbId}`} />
                        <Label
                          htmlFor={`match-${product.productId}-${candidate.qbId}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link2 className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">{candidate.qbName}</span>
                            {candidate.qbPrice != null && candidate.qbPrice > 0 && (
                              <span className="text-sm text-gray-500">${candidate.qbPrice.toFixed(2)}</span>
                            )}
                            <Badge
                              variant="outline"
                              className={
                                candidate.matchConfidence === 'exact'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : candidate.matchConfidence === 'high'
                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                              }
                            >
                              {candidate.matchConfidence === 'exact' ? 'best match' : candidate.matchConfidence === 'high' ? 'likely match' : 'possible'}
                            </Badge>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-medium text-gray-400 uppercase">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Create new in QuickBooks</p>
                    <div className="flex items-center space-x-3 p-3 border-2 border-dashed border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50/30 transition-colors">
                      <RadioGroupItem value="create_new" id={`create-new-${product.productId}`} />
                      <Label
                        htmlFor={`create-new-${product.productId}`}
                        className="flex-1 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-600" />
                          <span className="font-medium">Create new item</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Create a new QuickBooks item named "{product.productName}"
                        </p>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!allDecisionsMade || isProcessing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm & Send Invoices'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
